export interface FeatureStoragePayload {
  features: Array<{
    key: string;
    name: string;
    description?: string;
    planType: string;
    enabled: boolean;
    displayOrder: number;
  }>;
  enabledFeatures: string[];
  fetchedAt: number;
}

interface FeatureStorageUserContext {
  id?: string;
  role?: string;
  tenantId?: string | null;
}

const FEATURE_STORAGE_PREFIX = 'arrangelly:dashboard:features:v1:';
const FEATURE_SESSION_KEY = 'arrangelly:dashboard:feature-session';

const readStorage = (storage: Storage | null, key: string): string | null => {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (storage: Storage | null, key: string, value: string): void => {
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // Ignore storage write errors (private mode/quota)
  }
};

const removeStorage = (storage: Storage | null, key: string): void => {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage removal errors
  }
};

const getLocalStorage = (): Storage | null =>
  typeof window === 'undefined' ? null : window.localStorage;
const getSessionStorage = (): Storage | null =>
  typeof window === 'undefined' ? null : window.sessionStorage;

const getSelectedTenantId = (): string => {
  const sessionValue = readStorage(getSessionStorage(), 'selectedTenantId');
  if (sessionValue) return sessionValue;
  return readStorage(getLocalStorage(), 'selectedTenantId') || 'no-tenant';
};

export const getFeatureSessionVersion = (): string => {
  const sessionValue = readStorage(getSessionStorage(), FEATURE_SESSION_KEY);
  return sessionValue || '0';
};

const buildFeatureScope = (user?: FeatureStorageUserContext | null): string => {
  const userId = user?.id || 'anonymous';
  const role = (user?.role || 'unknown').toLowerCase();
  const tenantId = user?.tenantId || getSelectedTenantId();
  const sessionVersion = getFeatureSessionVersion();
  return `${userId}:${role}:${tenantId}:${sessionVersion}`;
};

export const getFeatureStorageKey = (user?: FeatureStorageUserContext | null): string =>
  `${FEATURE_STORAGE_PREFIX}${buildFeatureScope(user)}`;

export const loadStoredFeatures = (
  user?: FeatureStorageUserContext | null
): FeatureStoragePayload | null => {
  const key = getFeatureStorageKey(user);
  const raw =
    readStorage(getSessionStorage(), key) ||
    readStorage(getLocalStorage(), key);

  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as FeatureStoragePayload;
    if (!Array.isArray(parsed.features) || !Array.isArray(parsed.enabledFeatures)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const saveStoredFeatures = (
  payload: FeatureStoragePayload,
  user?: FeatureStorageUserContext | null
): void => {
  const key = getFeatureStorageKey(user);
  const encoded = JSON.stringify(payload);
  writeStorage(getSessionStorage(), key, encoded);
  writeStorage(getLocalStorage(), key, encoded);
};

export const clearAllStoredFeatures = (): void => {
  const local = getLocalStorage();
  const session = getSessionStorage();

  if (local) {
    for (let i = local.length - 1; i >= 0; i -= 1) {
      const key = local.key(i);
      if (key && key.startsWith(FEATURE_STORAGE_PREFIX)) {
        removeStorage(local, key);
      }
    }
  }

  if (session) {
    for (let i = session.length - 1; i >= 0; i -= 1) {
      const key = session.key(i);
      if (key && key.startsWith(FEATURE_STORAGE_PREFIX)) {
        removeStorage(session, key);
      }
    }
  }
};

export const resetFeatureStorageOnAuthChange = (): void => {
  clearAllStoredFeatures();
  const nextVersion = String(Date.now());
  writeStorage(getSessionStorage(), FEATURE_SESSION_KEY, nextVersion);
  writeStorage(getLocalStorage(), FEATURE_SESSION_KEY, nextVersion);
};
